import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Provider colors
const PROVIDER_COLORS = {
  'claude-cli': '#3B82F6',
  'gpt-5-mini': '#10B981',
};

const PROVIDER_LABELS = {
  'claude-cli': 'Claude CLI',
  'gpt-5-mini': 'GPT-5 mini',
};

// Helpers
const formatNumber = (num) => {
  if (num == null) return '0';
  return new Intl.NumberFormat('ko-KR').format(num);
};

const formatCost = (cost) => {
  if (cost == null) return '$0.0000';
  return `$${Number(cost).toFixed(4)}`;
};

const formatDateKST = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShortDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: 'short',
    day: 'numeric',
  });
};

const formatDuration = (ms) => {
  if (ms == null) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const getToken = () => {
  const match = document.cookie.match(/ilouli_token=([^;]+)/);
  return match ? match[1] : null;
};

const AiUsageTracker = ({ initialTab = 'overview' }) => {
  // State
  const [days, setDays] = useState(30);
  const [providerTab, setProviderTab] = useState('all');
  const [activeView, setActiveView] = useState(initialTab);
  const [expandedSession, setExpandedSession] = useState(null);

  // Data states
  const [summary, setSummary] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [byProject, setByProject] = useState([]);
  const [byModel, setByModel] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [health, setHealth] = useState(null);
  const [chartMode, setChartMode] = useState('daily'); // 'daily' or 'hourly'
  const [hourlyTrend, setHourlyTrend] = useState([]);
  const [scanResults, setScanResults] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedUnreg, setSelectedUnreg] = useState(new Set());
  const [registering, setRegistering] = useState(false);

  // Loading/error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch helper
  const fetchData = useCallback(async (endpoint, params = {}) => {
    const token = getToken();
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}/api/admin/ai-usage-tracking/${endpoint}${queryString ? `?${queryString}` : ''}`;

    const res = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }
    return res.json();
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [summaryData, trendData, projectData, modelData, recentData, featureData, healthData, hourlyData] = await Promise.allSettled([
        fetchData('summary', { days }),
        fetchData('daily-trend', { days }),
        fetchData('by-project', { days }),
        fetchData('by-model', { days }),
        fetchData('recent', { limit: 50 }),
        fetchData('features'),
        fetchData('health'),
        fetchData('hourly-trend', { days }),
      ]);

      if (summaryData.status === 'fulfilled') setSummary(summaryData.value);
      if (trendData.status === 'fulfilled') setDailyTrend(trendData.value || []);
      if (projectData.status === 'fulfilled') setByProject(projectData.value || []);
      if (modelData.status === 'fulfilled') setByModel(modelData.value || []);
      if (recentData.status === 'fulfilled') setRecentSessions(recentData.value || []);
      if (featureData.status === 'fulfilled') setFeatures(featureData.value || []);
      if (healthData.status === 'fulfilled') setHealth(healthData.value);
      if (hourlyData.status === 'fulfilled') setHourlyTrend(hourlyData.value || []);

      // Check if all failed
      const allFailed = [summaryData, trendData, projectData, modelData, recentData, featureData, healthData, hourlyData]
        .every(r => r.status === 'rejected');
      if (allFailed) {
        setError('API 연결에 실패했습니다. 백엔드가 실행 중인지 확인해주세요.');
      }
    } catch (err) {
      setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [days, fetchData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync active view with initialTab prop
  useEffect(() => {
    setActiveView(initialTab);
  }, [initialTab]);

  // Filter data by provider
  const filterByProvider = (data) => {
    if (providerTab === 'all') return data;
    return data.filter(item => item.provider === providerTab);
  };

  // Prepare chart data - merge daily trend into per-date rows
  const chartData = (() => {
    const dateMap = {};
    const filtered = providerTab === 'all' ? dailyTrend : dailyTrend.filter(d => d.provider === providerTab);

    filtered.forEach(item => {
      if (!dateMap[item.date]) {
        dateMap[item.date] = { date: item.date, 'claude-cli': 0, 'gpt-5-mini': 0 };
      }
      if (item.provider && dateMap[item.date][item.provider] !== undefined) {
        dateMap[item.date][item.provider] += item.tokens || 0;
      }
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  })();

  // Prepare hourly chart data
  const hourlyChartData = (() => {
    const hourMap = {};
    const filtered = providerTab === 'all' ? hourlyTrend : hourlyTrend.filter(d => d.provider === providerTab);

    // Initialize all 24 hours
    for (let h = 0; h < 24; h++) {
      hourMap[h] = { hour: h, label: `${String(h).padStart(2, '0')}시`, 'claude-cli': 0, 'gpt-5-mini': 0 };
    }

    filtered.forEach(item => {
      const h = item.hour;
      if (hourMap[h] && item.provider && hourMap[h][item.provider] !== undefined) {
        hourMap[h][item.provider] += item.tokens || 0;
      }
    });

    return Object.values(hourMap).sort((a, b) => a.hour - b.hour);
  })();

  // Scan projects for AI usage
  const scanProjects = async () => {
    setScanning(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/ai-usage-tracking/scan-projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Scan failed: ${res.status}`);
      const data = await res.json();
      setScanResults(data);
      setSelectedUnreg(new Set());
    } catch (err) {
      alert('스캔 실패: ' + err.message);
    } finally {
      setScanning(false);
    }
  };

  // Register selected features
  const registerFeatures = async () => {
    if (selectedUnreg.size === 0) return;
    setRegistering(true);
    try {
      // 선택된 키를 파싱하여 features 배열 구성
      const features = [];
      for (const proj of (scanResults?.projects || [])) {
        for (const item of (proj.unregistered || [])) {
          const key = `${proj.project}:${item.feature}:${item.provider}`;
          if (selectedUnreg.has(key)) {
            features.push({ project: proj.project, feature: item.feature, provider: item.provider, file: item.file });
          }
        }
      }
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/ai-usage-tracking/register-features`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ features }),
      });
      if (!res.ok) throw new Error(`Register failed: ${res.status}`);
      const data = await res.json();
      alert(`${data.registered}개 기능이 등록되었습니다.`);
      // 스캔 다시 실행하여 결과 갱신
      setSelectedUnreg(new Set());
      scanProjects();
    } catch (err) {
      alert('등록 실패: ' + err.message);
    } finally {
      setRegistering(false);
    }
  };

  // Toggle single unregistered item
  const toggleUnreg = (key) => {
    setSelectedUnreg(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle all unregistered for a project
  const toggleAllUnreg = (proj) => {
    const keys = (proj.unregistered || []).map(u => `${proj.project}:${u.feature}:${u.provider}`);
    setSelectedUnreg(prev => {
      const next = new Set(prev);
      const allSelected = keys.every(k => next.has(k));
      if (allSelected) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  // Toggle ALL unregistered across all projects
  const toggleAllProjects = () => {
    const allKeys = [];
    for (const proj of (scanResults?.projects || [])) {
      for (const u of (proj.unregistered || [])) {
        allKeys.push(`${proj.project}:${u.feature}:${u.provider}`);
      }
    }
    setSelectedUnreg(prev => {
      const allSelected = allKeys.length > 0 && allKeys.every(k => prev.has(k));
      return allSelected ? new Set() : new Set(allKeys);
    });
  };

  // Provider badge component
  const ProviderBadge = ({ provider }) => {
    const color = PROVIDER_COLORS[provider] || '#6B7280';
    const label = PROVIDER_LABELS[provider] || provider;
    return (
      <span
        className="inline-flex items-center gap-1 py-1 px-2 rounded text-xs font-semibold"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: color }} />
        {label}
      </span>
    );
  };

  // Custom tooltip for chart
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold text-[#1d1d1f] m-0 mb-2">{chartMode === 'hourly' ? label : formatShortDate(label)}</p>
        {payload.map((entry, idx) => (
          <div key={idx} className="flex items-center gap-2 text-[13px]">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-600">{PROVIDER_LABELS[entry.dataKey] || entry.dataKey}:</span>
            <span className="font-semibold text-[#1d1d1f]">{formatNumber(entry.value)} tokens</span>
          </div>
        ))}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4" />
        <p className="text-gray-500 text-sm">AI 사용량 데이터를 불러오는 중...</p>
      </div>
    );
  }

  // Error state
  if (error && !summary) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <span className="text-4xl mb-4">&#9888;</span>
        <p className="text-gray-600 text-sm mb-4">{error}</p>
        <button
          onClick={loadData}
          className="py-2.5 px-5 bg-[#1d1d1f] text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all hover:bg-[#333]"
        >
          다시 시도
        </button>
      </div>
    );
  }

  const byProviderSummary = summary?.by_provider || {};

  return (
    <div className="space-y-6">
      {/* Header: Days filter + View toggle */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-2">
          <button
            className={`py-1.5 px-3 rounded-full text-xs font-medium cursor-pointer transition-all border ${activeView === 'overview' ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-[#1d1d1f]'}`}
            onClick={() => setActiveView('overview')}
          >
            통합 현황
          </button>
          <button
            className={`py-1.5 px-3 rounded-full text-xs font-medium cursor-pointer transition-all border ${activeView === 'features' ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-[#1d1d1f]'}`}
            onClick={() => setActiveView('features')}
          >
            기능 사용처
          </button>
        </div>
        <div className="flex gap-2">
          {[7, 14, 30, 90].map(d => (
            <button
              key={d}
              className={`py-1.5 px-3 rounded-full text-xs font-medium cursor-pointer transition-all border ${days === d ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-[#1d1d1f]'}`}
              onClick={() => setDays(d)}
            >
              {d}일
            </button>
          ))}
        </div>
      </div>

      {/* Section A: Summary Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        {/* Total Sessions */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-px">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#128202;</span>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">총 세션 수</span>
          </div>
          <span className="block text-[28px] font-bold text-[#1d1d1f] leading-tight tracking-tight">
            {formatNumber(summary?.total_sessions)}
          </span>
          <div className="flex gap-3 mt-3">
            {Object.entries(byProviderSummary).map(([provider, data]) => (
              <div key={provider} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: PROVIDER_COLORS[provider] || '#6B7280' }} />
                <span>{PROVIDER_LABELS[provider] || provider}: {formatNumber(data?.sessions)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Total Tokens */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-px">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#128292;</span>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">총 토큰</span>
          </div>
          <span className="block text-[28px] font-bold text-[#1d1d1f] leading-tight tracking-tight">
            {formatNumber(summary?.total_tokens)}
          </span>
          <div className="flex gap-3 mt-3">
            {Object.entries(byProviderSummary).map(([provider, data]) => (
              <div key={provider} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: PROVIDER_COLORS[provider] || '#6B7280' }} />
                <span>{PROVIDER_LABELS[provider] || provider}: {formatNumber(data?.tokens)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Total Cost */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-px">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#128176;</span>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">환산 비용</span>
          </div>
          <span className="block text-[28px] font-bold text-[#1d1d1f] leading-tight tracking-tight">
            {formatCost(summary?.total_cost_usd)}
          </span>
          <div className="flex gap-3 mt-3">
            {Object.entries(byProviderSummary).map(([provider, data]) => (
              <div key={provider} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: PROVIDER_COLORS[provider] || '#6B7280' }} />
                <span>{PROVIDER_LABELS[provider] || provider}: {formatCost(data?.cost)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Average Duration */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-px">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#9201;</span>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">평균 응답시간</span>
          </div>
          <span className="block text-[28px] font-bold text-[#1d1d1f] leading-tight tracking-tight">
            {formatDuration(summary?.avg_duration_ms)}
          </span>
          {health && (
            <div className="flex gap-3 mt-3">
              {health.claude_cli && (
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className={`w-2 h-2 rounded-full inline-block ${health.claude_cli.available ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span>CLI: {health.claude_cli.available ? '정상' : '오프라인'}</span>
                </div>
              )}
              {health.gpt_mini && (
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className={`w-2 h-2 rounded-full inline-block ${health.gpt_mini.ok ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span>GPT: {health.gpt_mini.ok ? `${health.gpt_mini.latency}ms` : '오프라인'}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Section B: Provider Tabs */}
      <div className="flex gap-2">
        {['all', 'claude-cli', 'gpt-5-mini'].map(tab => (
          <button
            key={tab}
            className={`py-2 px-4 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
              providerTab === tab
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-[#1d1d1f]'
            }`}
            style={providerTab === tab ? {
              backgroundColor: tab === 'all' ? '#1d1d1f' : PROVIDER_COLORS[tab],
              borderColor: tab === 'all' ? '#1d1d1f' : PROVIDER_COLORS[tab],
            } : {}}
            onClick={() => setProviderTab(tab)}
          >
            {tab === 'all' ? '전체' : PROVIDER_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Overview Tab Content */}
      {activeView === 'overview' && (
        <>
          {/* Section C: Daily Trend Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">토큰 사용량</h3>
              <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
                <button
                  className={`py-1.5 px-3 rounded-md text-xs font-medium cursor-pointer transition-all border-none ${chartMode === 'daily' ? 'bg-white text-[#1d1d1f] shadow-sm' : 'bg-transparent text-gray-500 hover:text-[#1d1d1f]'}`}
                  onClick={() => setChartMode('daily')}
                >
                  일별
                </button>
                <button
                  className={`py-1.5 px-3 rounded-md text-xs font-medium cursor-pointer transition-all border-none ${chartMode === 'hourly' ? 'bg-white text-[#1d1d1f] shadow-sm' : 'bg-transparent text-gray-500 hover:text-[#1d1d1f]'}`}
                  onClick={() => setChartMode('hourly')}
                >
                  시간대별
                </button>
              </div>
            </div>
            {(chartMode === 'daily' ? chartData : hourlyChartData).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <span className="text-3xl mb-2">&#128202;</span>
                <p className="text-sm">차트 데이터가 없습니다</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={chartMode === 'daily' ? chartData : hourlyChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorClaude" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorGpt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey={chartMode === 'daily' ? 'date' : 'label'}
                    tickFormatter={chartMode === 'daily' ? formatShortDate : undefined}
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    formatter={(value) => PROVIDER_LABELS[value] || value}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '13px', paddingTop: '8px' }}
                  />
                  {(providerTab === 'all' || providerTab === 'claude-cli') && (
                    <Area
                      type="monotone"
                      dataKey="claude-cli"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      fill="url(#colorClaude)"
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2 }}
                    />
                  )}
                  {(providerTab === 'all' || providerTab === 'gpt-5-mini') && (
                    <Area
                      type="monotone"
                      dataKey="gpt-5-mini"
                      stroke="#10B981"
                      strokeWidth={2}
                      fill="url(#colorGpt)"
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2 }}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Section D: Project Usage Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="py-4 px-5 border-b border-gray-200">
              <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">프로젝트별 사용량</h3>
            </div>
            {filterByProvider(byProject).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <span className="text-3xl mb-2">&#128193;</span>
                <p className="text-sm">프로젝트 데이터가 없습니다</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">프로젝트</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">제공자</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">세션 수</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">토큰</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">비용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterByProvider(byProject)
                      .sort((a, b) => (b.tokens || 0) - (a.tokens || 0))
                      .map((row, idx) => (
                        <tr key={idx} className="transition-colors hover:bg-slate-50">
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] font-medium border-b border-gray-200">
                            {row.project || '-'}
                          </td>
                          <td className="py-2.5 px-4 text-[13px] border-b border-gray-200">
                            <ProviderBadge provider={row.provider} />
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatNumber(row.sessions)}
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatNumber(row.tokens)}
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatCost(row.cost_usd)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Model Usage */}
          {filterByProvider(byModel).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="py-4 px-5 border-b border-gray-200">
                <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">모델별 사용량</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">모델</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">제공자</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">세션 수</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">토큰</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">비용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterByProvider(byModel)
                      .sort((a, b) => (b.tokens || 0) - (a.tokens || 0))
                      .map((row, idx) => (
                        <tr key={idx} className="transition-colors hover:bg-slate-50">
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] font-medium border-b border-gray-200">
                            <code className="bg-gray-100 py-1 px-2 rounded text-[12px] font-mono">{row.model || '-'}</code>
                          </td>
                          <td className="py-2.5 px-4 text-[13px] border-b border-gray-200">
                            <ProviderBadge provider={row.provider} />
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatNumber(row.sessions)}
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatNumber(row.tokens)}
                          </td>
                          <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-medium border-b border-gray-200">
                            {formatCost(row.cost_usd)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section F: Recent Sessions Log */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="py-4 px-5 border-b border-gray-200">
              <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">최근 AI 호출 로그</h3>
            </div>
            {filterByProvider(recentSessions).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <span className="text-3xl mb-2">&#128196;</span>
                <p className="text-sm">최근 호출 기록이 없습니다</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full border-collapse min-w-[900px]">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-left border-b border-gray-200">시간</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-left border-b border-gray-200">제공자</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-left border-b border-gray-200">모델</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-left border-b border-gray-200">프로젝트</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-left border-b border-gray-200">기능</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-right border-b border-gray-200">토큰</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-right border-b border-gray-200">비용</th>
                      <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center border-b border-gray-200">상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterByProvider(recentSessions).map((session) => (
                      <React.Fragment key={session.id}>
                        <tr
                          className={`transition-colors cursor-pointer ${
                            session.is_error
                              ? 'bg-red-50 hover:bg-red-100'
                              : expandedSession === session.id
                                ? 'bg-blue-50'
                                : 'hover:bg-slate-50'
                          }`}
                          onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                        >
                          <td className="py-2 px-3 text-[12px] text-gray-500 border-b border-gray-200 whitespace-nowrap">
                            {formatDateKST(session.created_at)}
                          </td>
                          <td className="py-2 px-3 text-[13px] border-b border-gray-200">
                            <ProviderBadge provider={session.provider} />
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#1d1d1f] font-mono border-b border-gray-200">
                            {session.model || '-'}
                          </td>
                          <td className="py-2 px-3 text-[13px] text-[#1d1d1f] font-medium border-b border-gray-200">
                            {session.project || '-'}
                          </td>
                          <td className="py-2 px-3 text-[13px] text-gray-600 border-b border-gray-200">
                            {session.feature || '-'}
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#1d1d1f] text-right font-medium border-b border-gray-200 whitespace-nowrap">
                            {formatNumber(session.total_tokens)}
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#1d1d1f] text-right font-medium border-b border-gray-200 whitespace-nowrap">
                            {formatCost(session.cost_usd)}
                          </td>
                          <td className="py-2 px-3 text-center border-b border-gray-200">
                            {session.is_error ? (
                              <span className="inline-block py-0.5 px-2 rounded text-[11px] font-semibold bg-red-100 text-red-600">오류</span>
                            ) : (
                              <span className="inline-block py-0.5 px-2 rounded text-[11px] font-semibold bg-green-50 text-green-600">성공</span>
                            )}
                          </td>
                        </tr>
                        {/* Expanded detail row */}
                        {expandedSession === session.id && (
                          <tr>
                            <td colSpan={8} className="bg-slate-50 border-b border-gray-200 p-0">
                              <div className="py-3 px-5">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                                  <div>
                                    <span className="text-gray-500 block mb-0.5">Input 토큰</span>
                                    <span className="font-semibold text-[#1d1d1f]">{formatNumber(session.input_tokens)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 block mb-0.5">Output 토큰</span>
                                    <span className="font-semibold text-[#1d1d1f]">{formatNumber(session.output_tokens)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 block mb-0.5">응답시간</span>
                                    <span className="font-semibold text-[#1d1d1f]">{formatDuration(session.duration_ms)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 block mb-0.5">세션 ID</span>
                                    <span className="font-mono text-gray-600">{session.id}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Features Tab Content */}
      {activeView === 'features' && (
        <>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="py-4 px-5 border-b border-gray-200">
            <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">AI 기능 사용처</h3>
            <p className="text-sm text-gray-500 mt-1 mb-0">어떤 프로젝트에서 어떤 AI 기능이 사용되고 있는지 확인합니다.</p>
          </div>
          {filterByProvider(features).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <span className="text-3xl mb-2">&#128268;</span>
              <p className="text-sm">등록된 AI 기능이 없습니다</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[700px]">
                <thead>
                  <tr>
                    <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">기능</th>
                    <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">프로젝트</th>
                    <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">AI 제공자</th>
                    <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-left border-b border-gray-200">최근 사용</th>
                    <th className="bg-slate-50 font-semibold text-[11px] text-gray-600 uppercase tracking-wider py-2.5 px-4 text-right border-b border-gray-200">총 호출</th>
                  </tr>
                </thead>
                <tbody>
                  {filterByProvider(features)
                    .sort((a, b) => (b.total_calls || 0) - (a.total_calls || 0))
                    .map((feat, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-slate-50">
                        <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] font-medium border-b border-gray-200">
                          {feat.feature || '-'}
                        </td>
                        <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] border-b border-gray-200">
                          {feat.project || '-'}
                        </td>
                        <td className="py-2.5 px-4 text-[13px] border-b border-gray-200">
                          <ProviderBadge provider={feat.provider} />
                        </td>
                        <td className="py-2.5 px-4 text-[13px] text-gray-500 border-b border-gray-200">
                          {formatDateKST(feat.last_used)}
                        </td>
                        <td className="py-2.5 px-4 text-[13px] text-[#1d1d1f] text-right font-semibold border-b border-gray-200">
                          {formatNumber(feat.total_calls)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

          {/* Project Scan */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mt-6">
            <div className="py-4 px-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-[#1d1d1f] m-0 tracking-tight">AI 사용처 자동 스캔</h3>
                <p className="text-sm text-gray-500 mt-1 mb-0">전체 프로젝트를 스캔하여 AI API 사용 현황을 자동 감지합니다.</p>
              </div>
              <button
                onClick={scanProjects}
                disabled={scanning}
                className={`py-2.5 px-5 rounded-lg text-sm font-semibold cursor-pointer transition-all border-none ${
                  scanning
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-[#1d1d1f] text-white hover:bg-[#333] active:scale-95'
                }`}
              >
                {scanning ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin inline-block" />
                    스캔 중...
                  </span>
                ) : '프로젝트 스캔'}
              </button>
            </div>

            {scanResults && (
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
                  <span>스캔 시간: {scanResults.scanned_at}</span>
                  <span>|</span>
                  <span>감지된 프로젝트: {scanResults.total_projects}개</span>
                </div>

                <div className="space-y-4">
                  {scanResults.projects?.map((proj, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between py-3 px-4 bg-slate-50">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-[#1d1d1f] text-sm">{proj.projectFull}</span>
                          {proj.hasGptMini && (
                            <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded text-[11px] font-semibold bg-green-50 text-green-600">
                              GPT-5 mini 연동됨
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {proj.providers?.map(p => (
                            <ProviderBadge key={p} provider={p} />
                          ))}
                        </div>
                      </div>

                      {proj.aiFiles?.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-[13px]">
                            <thead>
                              <tr>
                                <th className="font-medium text-[11px] text-gray-500 uppercase py-2 px-4 text-left border-b border-gray-100">파일</th>
                                <th className="font-medium text-[11px] text-gray-500 uppercase py-2 px-4 text-left border-b border-gray-100">기능</th>
                                <th className="font-medium text-[11px] text-gray-500 uppercase py-2 px-4 text-left border-b border-gray-100">사용 프로바이더</th>
                              </tr>
                            </thead>
                            <tbody>
                              {proj.aiFiles.map((af, afi) =>
                                af.features.map((feat, fi) => (
                                  <tr key={`${afi}-${fi}`} className="hover:bg-slate-50">
                                    {fi === 0 ? (
                                      <td className="py-2 px-4 font-mono text-[12px] text-gray-600 border-b border-gray-100 align-top" rowSpan={af.features.length}>
                                        {af.file}
                                      </td>
                                    ) : null}
                                    <td className="py-2 px-4 border-b border-gray-100">
                                      <code className="bg-blue-50 text-blue-600 py-0.5 px-1.5 rounded text-[11px]">
                                        {typeof feat === 'string' ? feat : feat.name}
                                      </code>
                                    </td>
                                    <td className="py-2 px-4 border-b border-gray-100">
                                      <div className="flex flex-wrap gap-1">
                                        {(typeof feat === 'string' ? (af.providers || []) : feat.providers).map(p => (
                                          <ProviderBadge key={p} provider={p} />
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {proj.unregistered?.length > 0 && (
                        <div className="border-t border-amber-200 bg-amber-50">
                          <div className="py-2 px-4 flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-3.5 h-3.5 accent-amber-600"
                                checked={proj.unregistered.every(u => selectedUnreg.has(`${proj.project}:${u.feature}:${u.provider}`))}
                                onChange={() => toggleAllUnreg(proj)}
                              />
                              <span className="text-[12px] text-amber-700 font-medium">
                                미등록 기능 {proj.unregistered.length}개
                              </span>
                            </label>
                          </div>
                          <div className="px-4 pb-3">
                            <div className="flex flex-wrap gap-1.5">
                              {proj.unregistered.map((u, ui) => {
                                const key = `${proj.project}:${u.feature}:${u.provider}`;
                                return (
                                  <label
                                    key={ui}
                                    className={`inline-flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[12px] cursor-pointer transition-all border ${
                                      selectedUnreg.has(key)
                                        ? 'bg-amber-100 border-amber-300 text-amber-800'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-amber-200'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-3 h-3 accent-amber-600"
                                      checked={selectedUnreg.has(key)}
                                      onChange={() => toggleUnreg(key)}
                                    />
                                    <code className="text-[11px]">{u.feature}</code>
                                    <ProviderBadge provider={u.provider} />
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 등록 액션 바 */}
                {scanResults.projects?.some(p => p.unregistered?.length > 0) && (
                  <div className="mt-4 flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-gray-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-blue-600"
                        checked={(() => {
                          const allKeys = scanResults.projects.flatMap(p =>
                            (p.unregistered || []).map(u => `${p.project}:${u.feature}:${u.provider}`)
                          );
                          return allKeys.length > 0 && allKeys.every(k => selectedUnreg.has(k));
                        })()}
                        onChange={toggleAllProjects}
                      />
                      <span className="text-sm text-gray-700 font-medium">전체 선택</span>
                      {selectedUnreg.size > 0 && (
                        <span className="text-[12px] text-blue-600 font-semibold">({selectedUnreg.size}개 선택됨)</span>
                      )}
                    </label>
                    <button
                      onClick={registerFeatures}
                      disabled={selectedUnreg.size === 0 || registering}
                      className={`py-2 px-5 rounded-lg text-sm font-semibold transition-all border-none ${
                        selectedUnreg.size === 0 || registering
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95 cursor-pointer'
                      }`}
                    >
                      {registering ? '등록 중...' : `선택 등록 (${selectedUnreg.size}개)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AiUsageTracker;
